/* eslint-disable react/jsx-max-depth */
import { type ElementType, useEffect, useRef, useState } from 'react';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  IconAddCandidate,
  IconAlertTriangle,
  IconLeftArrow,
  IconMail,
  IconPhone,
  IconRepeat,
  IconRightArrow,
  IconSettings,
  IconSms,
  IconXClose,
  SlideIn,
} from '@/components';
import { CallTemplateSelect } from '@/components/CallTemplateSelect';
import { EmailTemplateSelect } from '@/components/EmailTemplateSelect';
import IconArrowDown from '@/components/Icons/icons-generated/IconArrowDown';
import { Select } from '@/components/Select';
import { SmsTemplateSelect } from '@/components/SmsTemplateSelect';
import {
  type InviteCandidateViaMailRequest,
  type ReminderGroupDto,
  SendInvitationChannel,
} from '@/services/api';

import { useShareToken } from '../admin-interview';
import {
  adminInterviewKeys,
  useGetInterviewQuery,
  useGetInterviewRemindersQuery,
} from '../admin-interview/useAdminInterviewQueries';
import {
  useCandidatesBulkInviteMutation,
  useGetInterviewInvitationSettingsQuery,
  usePutInterviewInvitationSettingsMutation,
} from './useCandidateTableMutation';

type InvitationSettingsProps = { interviewId: string | undefined };
export const InvitationSettings: React.FC<InvitationSettingsProps> = (
  props
) => {
  const settingsModalDisclosure = useDisclosure();
  const { t } = useTranslation(['components', 'common']);
  return (
    <Stack>
      <Button
        color="gray.600"
        onClick={() => settingsModalDisclosure.onOpen()}
        leftIcon={<IconSettings color="gray.600" fontSize={20} />}
      >
        {t('components:settings.messageSettings')}
      </Button>
      <NotificationSettingsModal
        isOpen={settingsModalDisclosure.isOpen}
        onClose={settingsModalDisclosure.onClose}
        interviewId={props.interviewId}
      />
    </Stack>
  );
};

type ReminderGroup = {
  remindDate?: string;
  emailTemplateId?: string;
  emailTemplateName?: string;
  smsTemplateId?: string;
  smsTemplateName?: string;
  phoneCallTemplateId?: string;
  phoneCallTemplateName?: string;
  candidates?: Array<{
    id?: string;
    name?: string;
    surname?: string;
    email?: string;
  }>;
};

type CandidateListModalProps = {
  isOpen: boolean;
  onClose: () => void;
  reminder: {
    id: string;
    candidateId?: string;
    emailTemplateId?: string;
    smsTemplateId?: string;
    phoneCallTemplateId?: string;
    emailTemplateName?: string;
    smsTemplateName?: string;
    phoneCallTemplateName?: string;
  } | null;
  manualReminders: unknown;
};

const CandidateListModal: React.FC<CandidateListModalProps> = ({
  isOpen,
  onClose,
  reminder,
  manualReminders,
}) => {
  const { t } = useTranslation(['components', 'common']);

  const reminderGroups: Array<{
    remindDate?: string;
    emailTemplateId?: string;
    emailTemplateName?: string;
    smsTemplateId?: string;
    smsTemplateName?: string;
    phoneCallTemplateId?: string;
    phoneCallTemplateName?: string;
    candidates?: Array<{
      id?: string;
      name?: string;
      surname?: string;
      email?: string;
    }>;
  }> = [];

  if (Array.isArray(manualReminders)) {
    reminderGroups.push(...manualReminders);
  }

  const matchingGroup = reminderGroups.find((group) => {
    if (!reminder) return false;

    return (
      (reminder.emailTemplateId &&
        group.emailTemplateId === reminder.emailTemplateId) ??
      (reminder.smsTemplateId &&
        group.smsTemplateId === reminder.smsTemplateId) ??
      (reminder.phoneCallTemplateId &&
        group.phoneCallTemplateId === reminder.phoneCallTemplateId)
    );
  });

  const allCandidates = (matchingGroup?.candidates ?? []).reduce(
    (acc, candidate) => {
      if (candidate?.id) {
        const existingCandidate = acc.find((c) => c.id === candidate.id);
        if (!existingCandidate) {
          const fullName =
            `${candidate.name ?? ''} ${candidate.surname ?? ''}`.trim();
          const initials = getInitials(fullName);
          acc.push({
            id: candidate.id,
            name: fullName ?? candidate.email ?? 'Unknown',
            initials,
          });
        }
      }
      return acc;
    },
    [] as Array<{ id: string; name: string; initials: string }>
  );

  function getInitials(name: string): string {
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);
  }

  return (
    <Modal
      motionPreset="slideInRight"
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="16px"
        pos="absolute"
        top="16px"
        right="24px"
        bottom="16px"
        m={0}
        w="538px"
        maxW="538px"
        maxHeight="none"
        p={0}
      >
        <ModalHeader
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={6}
          py={4}
          borderBottom="1px solid"
          borderBottomColor="#E9EAED"
        >
          <HStack spacing={3}>
            <Button
              size="sm"
              variant="ghost"
              py={1.5}
              px={2}
              minW="auto"
              h="auto"
              borderRadius="24px"
              border="1px solid"
              borderColor="#E9EAED"
              onClick={onClose}
              bg="white"
            >
              <IconLeftArrow fontSize="14px" />
            </Button>
            <Text
              fontSize="18px"
              fontWeight="600"
              color="brandGray.900"
              textAlign="center"
              flex={1}
            >
              {t('components:settings.sentCandidates')} ({allCandidates.length})
            </Text>
          </HStack>
          <Button
            size="sm"
            variant="ghost"
            py={1.5}
            px={2}
            minW="auto"
            h="auto"
            borderRadius="24px"
            border="1px solid"
            borderColor="#E9EAED"
            onClick={onClose}
            bg="white"
          >
            <IconXClose fontSize="20px" />
          </Button>
        </ModalHeader>

        <ModalBody p={6} overflow="auto">
          <VStack spacing={1} align="stretch">
            {allCandidates.map((candidate, index) => (
              <Box key={candidate.id}>
                <HStack spacing={3} py={1}>
                  <Box
                    w="36px"
                    h="36px"
                    bg="primary.50"
                    borderRadius="200px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      color="primary.500"
                      textAlign="center"
                    >
                      {candidate.initials}
                    </Text>
                  </Box>
                  <Text
                    fontSize="16px"
                    fontWeight="500"
                    color="brandGray.900"
                    flex={1}
                  >
                    {candidate.name}
                  </Text>
                </HStack>
                {index < allCandidates.length - 1 && (
                  <Box h="1px" bg="#E9EAED" w="442px" ml="48px" mt={1} mb={1} />
                )}
              </Box>
            ))}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type ManualTriggeringTabProps = {
  interviewId?: string;
  manualReminders: Array<ReminderGroupDto>;
  isLoading: boolean;
};

const ManualTriggeringTab: React.FC<ManualTriggeringTabProps> = ({
  interviewId,
  manualReminders,
  isLoading,
}) => {
  const candidateListModal = useDisclosure();
  const [selectedReminder, setSelectedReminder] = useState<{
    id: string;
    candidateId?: string;
    emailTemplateId?: string;
    smsTemplateId?: string;
    phoneCallTemplateId?: string;
    emailTemplateName?: string;
    smsTemplateName?: string;
    phoneCallTemplateName?: string;
  } | null>(null);
  const [loadingReminderId, setLoadingReminderId] = useState<string | null>(
    null
  );
  const [showWarning, setShowWarning] = useState(false);
  const warningRef = useRef<HTMLDivElement>(null);

  const toast = useToast();
  const { t } = useTranslation(['components', 'common', 'auth']);

  const resendInvitesMutation = useCandidatesBulkInviteMutation({
    onSuccess() {
      setLoadingReminderId(null);
      toast({
        title: t('components:settings.invitesResent'),
        status: 'success',
      });
    },
    onError() {
      setLoadingReminderId(null);
      toast({ title: 'Error', status: 'error' });
    },
  });

  const generateReminderId = (reminderGroup: ReminderGroup): string => {
    return `${reminderGroup.emailTemplateId ?? ''}-${reminderGroup.smsTemplateId ?? ''}-${reminderGroup.phoneCallTemplateId ?? ''}-${reminderGroup.remindDate ?? ''}`;
  };

  const checkIfCanResend = (reminderGroup: ReminderGroup): boolean => {
    if (!reminderGroup.remindDate) return true;

    const reminderDate = new Date(reminderGroup.remindDate);
    const now = new Date();
    const oneHourInMs = 1.5 * 60 * 60 * 1000;

    return now.getTime() - reminderDate.getTime() >= oneHourInMs;
  };

  const handleResend = async (reminderGroup: {
    emailTemplateId?: string;
    smsTemplateId?: string;
    phoneCallTemplateId?: string;
    candidates?: Array<{ id?: string }>;
    remindDate?: string;
  }) => {
    if (!interviewId || !reminderGroup.candidates?.length) return;

    const reminderId = generateReminderId(reminderGroup as ReminderGroup);

    if (!checkIfCanResend(reminderGroup as ReminderGroup)) {
      setShowWarning(true);

      setTimeout(() => {
        warningRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);

      return;
    }

    setLoadingReminderId(reminderId);

    const uniqueCandidateIds = Array.from(
      new Set(
        reminderGroup.candidates
          .map((candidate) => candidate.id)
          .filter(Boolean)
      )
    ) as Array<string>;

    if (uniqueCandidateIds.length === 0) return;

    const body: InviteCandidateViaMailRequest = {
      candidateIds: uniqueCandidateIds,
    };

    if (reminderGroup.emailTemplateId) {
      body.channel = SendInvitationChannel.Email;
      body.mailTemplateId = reminderGroup.emailTemplateId;
    }
    if (reminderGroup.smsTemplateId) {
      body.channel = SendInvitationChannel.Sms;
      body.smsTemplateId = reminderGroup.smsTemplateId;
    }
    if (reminderGroup.phoneCallTemplateId) {
      body.channel = SendInvitationChannel.Call;
      body.phoneCallTemplateId = reminderGroup.phoneCallTemplateId;
    }

    await resendInvitesMutation.mutateAsync({ interviewId, ...body });
  };

  const reminderGroups: Array<ReminderGroup> = [];

  if (Array.isArray(manualReminders)) {
    reminderGroups.push(...manualReminders);
  }

  const emailReminders = reminderGroups.filter(
    (group) => group.emailTemplateId
  );
  const smsReminders = reminderGroups.filter((group) => group.smsTemplateId);
  const callReminders = reminderGroups.filter(
    (group) => group.phoneCallTemplateId
  );

  const renderReminderCard = (reminderGroup: ReminderGroup) => {
    const uniqueCandidates =
      reminderGroup.candidates?.reduce(
        (acc, candidate) => {
          if (candidate?.id && !acc.some((c) => c.id === candidate.id)) {
            acc.push({
              id: candidate.id,
              name: candidate.name,
              surname: candidate.surname,
              email: candidate.email,
            });
          }
          return acc;
        },
        [] as Array<{
          id: string;
          name?: string;
          surname?: string;
          email?: string;
        }>
      ) ?? [];

    const candidateCount = uniqueCandidates.length;

    return (
      <VStack
        key={`${reminderGroup.emailTemplateId}-${reminderGroup.smsTemplateId}-${reminderGroup.phoneCallTemplateId}-${reminderGroup.remindDate}`}
        spacing={2}
        bg="#FAFBFB"
        borderRadius="12px"
        border="1px solid"
        borderColor="#E9EAED"
        p={3}
        mb={3}
      >
        <HStack spacing={3} align="stretch" w="full">
          <VStack spacing={1.5} align="start" flex={1}>
            <Text fontSize="14px" color="#4F555C" fontWeight="500">
              {t('components:settings.messageSendDate')}
            </Text>
            <Box
              bg="#E9EAED"
              border="1px solid"
              borderColor="#DEDFE2"
              borderRadius="8px"
              px={3}
              py={2}
              w="full"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text fontSize="14px" color="gray.500">
                {reminderGroup.remindDate &&
                  new Date(reminderGroup.remindDate)
                    .toLocaleDateString('tr-TR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                    .replace(',', ' -')}
              </Text>
              <IconArrowDown fontSize={14} color="gray.500" />
            </Box>
          </VStack>
          <VStack spacing={1.5} align="start" flex={1}>
            <Text fontSize="14px" color="#4F555C" fontWeight="500">
              {t('components:settings.creationCompany')}
            </Text>
            <Box
              bg="#E9EAED"
              border="1px solid"
              borderColor="#DEDFE2"
              borderRadius="8px"
              px={3}
              py={2}
              w="full"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text fontSize="14px" color="gray.500" noOfLines={1}>
                {reminderGroup.emailTemplateName ??
                  reminderGroup.smsTemplateName ??
                  reminderGroup.phoneCallTemplateName ??
                  'Interview info'}
              </Text>
              <IconArrowDown fontSize={14} color="gray.500" />
            </Box>
          </VStack>
        </HStack>

        <HStack spacing={3} align="stretch" w="full">
          <VStack spacing={1.5} align="start" flex={1}>
            <Text fontSize="14px" color="#4F555C" fontWeight="500">
              {t('components:settings.sentCandidates')}
            </Text>
            <Box
              bg="white"
              border="1px solid"
              borderColor="#DEDFE2"
              borderRadius="8px"
              px={3}
              py={1}
              w="full"
              display="flex"
              alignItems="center"
              gap={2}
              cursor="pointer"
              _hover={{ bg: 'gray.50' }}
              onClick={() => {
                setSelectedReminder({
                  id: `${reminderGroup.emailTemplateId}-${reminderGroup.smsTemplateId}-${reminderGroup.phoneCallTemplateId}`,
                  emailTemplateId: reminderGroup.emailTemplateId,
                  smsTemplateId: reminderGroup.smsTemplateId,
                  phoneCallTemplateId: reminderGroup.phoneCallTemplateId,
                  emailTemplateName: reminderGroup.emailTemplateName,
                  smsTemplateName: reminderGroup.smsTemplateName,
                  phoneCallTemplateName: reminderGroup.phoneCallTemplateName,
                });
                candidateListModal.onOpen();
              }}
            >
              <IconAddCandidate fontSize="18px" color="gray.500" />
              <Text fontSize="14px" color="brandGray.900" flex={1}>
                {candidateCount === 1
                  ? uniqueCandidates[0]?.name
                  : candidateCount > 1
                    ? `${uniqueCandidates[0]?.name} + ${
                        candidateCount - 1
                      } ${t('common:person')}`
                    : t('common:person')}
              </Text>
              <IconRightArrow fontSize={16} color="gray.500" />
            </Box>
          </VStack>
          <VStack spacing={1.5} align="start" flex={1}>
            <Box h="20px" />
            <Button
              leftIcon={<IconRepeat fontSize="20px" />}
              size="sm"
              variant="outline"
              colorScheme="purple"
              borderColor="#DEDFE2"
              color="primary.500"
              fontWeight="600"
              fontSize="14px"
              w="full"
              borderRadius="8px"
              px={3}
              py={1.5}
              h="auto"
              isLoading={
                loadingReminderId === generateReminderId(reminderGroup)
              }
              onClick={() => handleResend(reminderGroup)}
            >
              {t('auth:otp.resend')}
            </Button>
          </VStack>
        </HStack>
      </VStack>
    );
  };

  if (isLoading) {
    return (
      <VStack spacing={4} align="stretch" justify="center" w="full">
        <Spinner size="sm" />
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {showWarning && (
        <Alert
          ref={warningRef}
          status="warning"
          bg="#FFFAF5"
          border="1px solid"
          borderColor="#FAF1E8"
          borderRadius="12px"
          p={2}
        >
          <Box
            bg="#FAF1E8"
            borderRadius="full"
            w="32px"
            h="32px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mr={3}
            mb={5}
          >
            <IconAlertTriangle color="#CA7219" fontSize={22} />
          </Box>
          <Box flex={1}>
            <Text fontSize="16px" fontWeight="500" color="#2F3236" mb={1}>
              {t('components:settings.tooManyResendRequests')}
            </Text>
            <AlertDescription
              fontSize="12px"
              color="#656D75"
              pl="44px"
              mt={-2}
              ml="-44px"
            >
              {t('components:settings.pleaseWaitAndTryAgain')}
            </AlertDescription>
          </Box>
          <CloseButton
            size="sm"
            color="#6F7881"
            onClick={() => setShowWarning(false)}
          />
        </Alert>
      )}

      <Accordion allowMultiple w="full">
        <AccordionItem
          border="1px solid"
          borderColor="gray.50"
          borderRadius="lg"
          mb={3}
        >
          <AccordionButton p={4}>
            <HStack flex="1" spacing={3}>
              <Icon as={IconMail} boxSize={5} color="gray.500" />
              <Text fontWeight="medium">
                {t('common:information.email')} ({emailReminders.length})
              </Text>
            </HStack>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4}>
            {emailReminders.length > 0 ? (
              emailReminders.map(renderReminderCard)
            ) : (
              <Text color="gray.500" fontSize="sm">
                {t('components:settings.noEmailRemindersFound')}
              </Text>
            )}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem
          border="1px solid"
          borderColor="gray.50"
          borderRadius="lg"
          mb={3}
        >
          <AccordionButton p={4}>
            <HStack flex="1" spacing={3}>
              <Icon as={IconSms} boxSize={5} color="gray.500" />
              <Text fontWeight="medium">
                {t('common:information.sms')} ({smsReminders.length})
              </Text>
            </HStack>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4}>
            {smsReminders.length > 0 ? (
              smsReminders.map(renderReminderCard)
            ) : (
              <Text color="gray.500" fontSize="sm">
                {t('components:settings.noSmsRemindersFound')}
              </Text>
            )}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem
          border="1px solid"
          borderColor="gray.50"
          borderRadius="lg"
        >
          <AccordionButton p={4}>
            <HStack flex="1" spacing={3}>
              <Icon as={IconSms} boxSize={5} color="gray.500" />
              <Text fontWeight="medium">
                {t('common:call.call')} ({callReminders.length})
              </Text>
            </HStack>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4}>
            {callReminders.length > 0 ? (
              callReminders.map(renderReminderCard)
            ) : (
              <Text color="gray.500" fontSize="sm">
                {t('components:settings.noCallRemindersFound')}
              </Text>
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <CandidateListModal
        isOpen={candidateListModal.isOpen}
        onClose={candidateListModal.onClose}
        reminder={selectedReminder}
        manualReminders={manualReminders}
      />
    </VStack>
  );
};

type NotificationSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  interviewId: string | undefined;
};

const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = (
  props
) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['components', 'common']);

  const { mutateAsync } = usePutInterviewInvitationSettingsMutation({
    onSuccess() {
      toast({
        title: t('components:settings.settingsChanged'),
        status: 'success',
      });
      queryClient.invalidateQueries({
        queryKey: adminInterviewKeys.getInterview({
          interviewsId: props.interviewId!,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: ['getInterviewInvitationSettings', props.interviewId!],
      });
    },
  });

  const shareToken = useShareToken();
  const interviewData = useGetInterviewQuery(
    {
      interviewsId: props.interviewId!,
    },
    {
      enabled: !!props.interviewId,
      axiosRequestConfig: {
        overrideSecurityToken: shareToken,
      },
    }
  );

  const { data: manualReminders, isLoading: isManualRemindersLoading } =
    useGetInterviewRemindersQuery(
      {
        interviewId: props.interviewId!,
        isManual: true,
        page: 0,
        size: 100,
      },
      {
        enabled: !!props.interviewId,
      }
    );

  const invitationSettingsData = useGetInterviewInvitationSettingsQuery(
    {
      interviewsId: props.interviewId!,
    },
    {
      enabled: !!props.interviewId,
    }
  );

  const changeInterviewInvitationSettings = async (
    settingsKey: string,
    settingValue: boolean | number | string
  ) => {
    if (!props.interviewId) return;
    await mutateAsync({
      interviewsId: props.interviewId,
      body: {
        ...invitationSetting,
        [settingsKey]: settingValue,
      },
    });
  };

  const invitationSetting =
    invitationSettingsData?.data ??
    interviewData?.data?.interview?.invitationSetting;

  const dayOptions = Array.from({ length: 31 }, (_, i) => ({
    label:
      i === 0
        ? t('common:timeAndDate.day') +
          ' (' +
          t('common:timeAndDate.immediately') +
          ')'
        : `${i} ${i === 1 ? t('common:timeAndDate.day') : t('common:timeAndDate.days')}`,
    value: i,
  }));

  const hourOptions = Array.from({ length: 25 }, (_, i) => ({
    label:
      i === 0
        ? t('common:timeAndDate.hour') +
          ' (' +
          t('common:timeAndDate.noDelay') +
          ')'
        : i === 1
          ? `1 ${t('common:timeAndDate.hour')}`
          : t('common:timeAndDate.hour_other', { count: i }),
    value: i,
  }));

  return (
    <Modal
      motionPreset="slideInRight"
      isOpen={props.isOpen}
      onClose={props.onClose}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="xl"
        alignItems="center"
        pos="absolute"
        top="16px"
        right="24px"
        bottom="16px"
        m={0}
        maxHeight="none"
        maxWidth="538px"
      >
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />
        <ModalHeader
          w="full"
          justifyItems="start"
          borderBottom="1px solid"
          borderBottomColor="gray.50"
        >
          <Text fontWeight="medium">
            {t('components:settings.messageSettings')}
          </Text>
        </ModalHeader>

        <ModalBody pb={2} w="538px">
          <SlideIn>
            <Tabs colorScheme="purple" defaultIndex={0}>
              <TabList borderColor="gray.50">
                <Tab fontSize={14} fontWeight={500}>
                  {t('components:settings.systemMessageTriggering')}
                  <Box
                    as="span"
                    bg="gray.50"
                    color="gray.600"
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    ml={2}
                    fontSize="xs"
                    fontWeight="500"
                  >
                    3
                  </Box>
                </Tab>
                <Tab fontSize={14} lineHeight={2} fontWeight={500}>
                  {t('components:settings.manualTriggering')}
                  <Box
                    as="span"
                    bg="gray.50"
                    color="gray.600"
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    ml={2}
                    fontSize="xs"
                    fontWeight="500"
                  >
                    {manualReminders?.data?.length}
                  </Box>
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <Text color="gray.900" fontWeight="semibold">
                      {t('components:settings.systemMessageTriggering')}
                    </Text>
                    <Stack spacing={3}>
                      <NotificationCard
                        title={t('components:adminCandidates.filters.email')}
                        onChangeSettings={changeInterviewInvitationSettings}
                        isEnabled={invitationSetting?.emailEnabled ?? false}
                        icon={IconMail}
                        field="emailEnabled"
                        placeholderDesc={t('components:settings.emailSendDate')}
                        remindDays={invitationSetting?.emailRemindDays}
                        remindHours={invitationSetting?.emailRemindHours}
                        daysField="emailRemindDays"
                        hoursField="emailRemindHours"
                        dayOptions={dayOptions}
                        hourOptions={hourOptions}
                        templateId={invitationSetting?.reminderEmailTemplateId}
                        templateField="reminderEmailTemplateId"
                        templateType="email"
                      />
                      <NotificationCard
                        onChangeSettings={changeInterviewInvitationSettings}
                        isEnabled={invitationSetting?.smsEnabled ?? false}
                        field="smsEnabled"
                        title={t('common:information.sms')}
                        icon={IconSms}
                        placeholderDesc={t('components:settings.smsSendDate', {
                          hour: '8:30',
                        })}
                        remindDays={invitationSetting?.smsRemindDays}
                        remindHours={invitationSetting?.smsRemindHours}
                        daysField="smsRemindDays"
                        hoursField="smsRemindHours"
                        dayOptions={dayOptions}
                        hourOptions={hourOptions}
                        templateId={invitationSetting?.reminderSmsTemplateId}
                        templateField="reminderSmsTemplateId"
                        templateType="sms"
                      />
                      <NotificationCard
                        onChangeSettings={changeInterviewInvitationSettings}
                        field="phoneCallEnabled"
                        isEnabled={invitationSetting?.phoneCallEnabled ?? false}
                        title={t('common:call.call')}
                        icon={IconPhone}
                        placeholderDesc={t('components:settings.callSendDate', {
                          hour: '18:30',
                        })}
                        remindDays={invitationSetting?.callRemindDays}
                        remindHours={invitationSetting?.callRemindHours}
                        daysField="callRemindDays"
                        hoursField="callRemindHours"
                        dayOptions={dayOptions}
                        hourOptions={hourOptions}
                        templateId={invitationSetting?.reminderCallTemplateId}
                        templateField="reminderCallTemplateId"
                        templateType="call"
                      />
                    </Stack>
                  </VStack>
                </TabPanel>
                <TabPanel px={0}>
                  <ManualTriggeringTab
                    interviewId={props.interviewId}
                    manualReminders={manualReminders?.data ?? []}
                    isLoading={isManualRemindersLoading}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </SlideIn>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

type NotificationCardProps = {
  title: string;
  icon: ElementType;
  onChangeSettings: (
    settingsKey: string,
    settingValue: boolean | number | string
  ) => void;
  isEnabled: boolean;
  field: string;
  placeholderDesc: string;
  remindDays?: number;
  remindHours?: number;
  daysField: string;
  hoursField: string;
  dayOptions: Array<{ label: string; value: number }>;
  hourOptions: Array<{ label: string; value: number }>;
  templateId?: string;
  templateField: string;
  templateType: 'email' | 'sms' | 'call';
};

const NotificationCard: React.FC<NotificationCardProps> = ({
  title,
  icon,
  onChangeSettings,
  isEnabled,
  field,
  placeholderDesc,
  remindDays,
  remindHours,
  daysField,
  hoursField,
  dayOptions,
  hourOptions,
  templateId,
  templateField,
  templateType,
}) => {
  const [isActive, setIsActive] = useState(isEnabled);
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >(templateId);
  const [localRemindDays, setLocalRemindDays] = useState(remindDays ?? 0);
  const [localRemindHours, setLocalRemindHours] = useState(remindHours ?? 0);
  const [isSavingDay, setIsSavingDay] = useState(false);
  const [isSavingHour, setIsSavingHour] = useState(false);
  const { t } = useTranslation(['components', 'common']);

  useEffect(() => {
    setSelectedTemplateId(templateId);
  }, [templateId]);

  useEffect(() => {
    setLocalRemindDays(remindDays ?? 0);
    setIsSavingDay(false);
  }, [remindDays]);

  useEffect(() => {
    setLocalRemindHours(remindHours ?? 0);
    setIsSavingHour(false);
  }, [remindHours]);

  const buildDescription = (): string => {
    if (!localRemindDays && !localRemindHours) {
      return placeholderDesc;
    }

    const parts: Array<string> = [];

    if (localRemindDays) {
      parts.push(
        `${localRemindDays} ${
          localRemindDays === 1
            ? t('common:timeAndDate.day')
            : t('common:timeAndDate.days')
        }`
      );
    }

    if (localRemindHours) {
      parts.push(
        `${
          localRemindHours === 1
            ? 1 + t('common:timeAndDate.hour')
            : t('common:timeAndDate.hour_other', {
                count: localRemindHours,
                defaultValue: 'hours',
              })
        }`
      );
    }

    const timeText = parts.join(' and ');

    return t('components:settings.reminderSendDate', { timeText });
  };

  const renderTemplateSelect = () => {
    switch (templateType) {
      case 'email':
        return (
          <EmailTemplateSelect
            selectedTemplateId={selectedTemplateId}
            setSelectedTemplateId={(newTemplateId) => {
              setSelectedTemplateId(newTemplateId);
              const resolvedValue =
                typeof newTemplateId === 'function'
                  ? newTemplateId(selectedTemplateId)
                  : newTemplateId;
              onChangeSettings(templateField, resolvedValue ?? '');
            }}
            showLabel
            isOptional
            size="sm"
          />
        );
      case 'sms':
        return (
          <SmsTemplateSelect
            selectedTemplateId={selectedTemplateId}
            setSelectedTemplateId={(newTemplateId) => {
              setSelectedTemplateId(newTemplateId);
              const resolvedValue =
                typeof newTemplateId === 'function'
                  ? newTemplateId(selectedTemplateId)
                  : newTemplateId;
              onChangeSettings(templateField, resolvedValue ?? '');
            }}
            showLabel
            isOptional
            size="sm"
          />
        );
      case 'call':
        return (
          <CallTemplateSelect
            selectedTemplateId={selectedTemplateId}
            setSelectedTemplateId={(newTemplateId) => {
              setSelectedTemplateId(newTemplateId);
              const resolvedValue =
                typeof newTemplateId === 'function'
                  ? newTemplateId(selectedTemplateId)
                  : newTemplateId;
              onChangeSettings(templateField, resolvedValue ?? '');
            }}
            showLabel
            isOptional
            size="sm"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.50"
      p={4}
      w="full"
      boxShadow="sm"
    >
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <Icon as={icon} boxSize={5} fontSize={22} color="gray.500" />
          <Text fontWeight="medium">{title}</Text>
        </HStack>
        <HStack spacing={2}>
          <Switch
            colorScheme="purple"
            isChecked={isActive}
            onChange={() => {
              onChangeSettings(field, !isActive);
              setIsActive(!isActive);
            }}
          />
          <Text color="purple.600" fontWeight="medium">
            {t('components:status.active')}
          </Text>
        </HStack>
      </Flex>

      <VStack spacing={3} align="stretch">
        <HStack spacing={3}>
          <VStack flex={1} spacing={1} align="start">
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              {t('common:timeAndDate.days')}
            </Text>
            <Select
              size="sm"
              isLoading={isSavingDay}
              value={dayOptions.find(
                (option) => option.value === localRemindDays
              )}
              onChange={(option) => {
                if (option) {
                  setIsSavingDay(true);
                  setLocalRemindDays(option.value);
                  Promise.resolve(onChangeSettings(daysField, option.value))
                    .catch(() => {})
                    .finally(() => setIsSavingDay(false));
                }
              }}
              options={dayOptions}
              placeholder={t('common:timeAndDate.days')}
            />
          </VStack>

          <VStack flex={1} spacing={1} align="start">
            <Text fontSize="sm" color="gray.700" fontWeight="medium">
              {t('common:timeAndDate.hour')}
            </Text>
            <Select
              size="sm"
              isLoading={isSavingHour}
              value={hourOptions.find(
                (option) => option.value === localRemindHours
              )}
              onChange={(option) => {
                if (option) {
                  setIsSavingHour(true);
                  setLocalRemindHours(option.value);
                  Promise.resolve(onChangeSettings(hoursField, option.value))
                    .catch(() => {})
                    .finally(() => setIsSavingHour(false));
                }
              }}
              options={hourOptions}
              placeholder={t('common:timeAndDate.hour')}
            />
          </VStack>
        </HStack>

        <VStack spacing={1} align="start">
          {renderTemplateSelect()}
        </VStack>

        <HStack
          bg="gray.25"
          borderRadius="md"
          justify="space-between"
          align="center"
          borderColor="gray.50"
          borderWidth={1}
          px={4}
          py={3}
        >
          <VStack flex={1} spacing={1} align="start">
            <Text fontSize="sm" color="gray.500">
              {t('components:settings.messageSendDate')}
            </Text>
            <Text fontWeight="medium" fontSize="sm">
              {buildDescription()}
            </Text>
          </VStack>
        </HStack>
      </VStack>
    </Box>
  );
};
